<script setup lang="ts">
import IconMaterialSymbolsCalendarTodayRounded from '~icons/material-symbols/calendar-today-rounded'

// Components
import ColorSquare from '@/shared/components/ColorSquare.vue'
import ValueBox from '@/shared/components/ValueBox.vue'
import Icon from '@/shared/components/Icon.vue'

// Types
import type { TooltipRichContent } from '@/shared/directives/tooltip'
import type { Component } from 'vue'

// Body of a label/value/batch tooltip, shared by the v-tooltip host and the
// roadmap hover tooltip so both read the same. Renders no tooltip chrome.
// The `header` slot replaces the default icon + text row for callers that
// need a custom one (e.g. the roadmap's warning header).
type Props = {
  icon?: Component
  header?: string
  items: TooltipRichContent['items']
  batches?: TooltipRichContent['batches']
}

const props = defineProps<Props>()
</script>

<template>
  <slot name="header">
    <div
      v-if="props.header"
      class="flex items-center gap-1.5 pb-1 leading-none font-semibold"
    >
      <Icon
        :icon="props.icon ?? IconMaterialSymbolsCalendarTodayRounded"
        width="14"
        height="14"
        class="align-middle"
      />
      <span>{{ props.header }}</span>
    </div>
  </slot>
  <div
    v-for="(item, index) in props.items"
    :key="index"
    class="flex items-center"
    :class="{ 'mt-1.5': item.gapAbove }"
  >
    <span class="text-muted-foreground">{{ item.label }}:</span>
    <ValueBox
      :value="item.value"
      :unit="item.unit"
      :input-unit="item.inputUnit"
      :class="{ 'text-success': item.tone === 'positive', 'text-destructive': item.tone === 'negative' }"
    />
  </div>
  <div
    v-if="props.batches?.length"
    class="flex items-center gap-1 pt-0.5"
  >
    <span class="text-muted-foreground">Batch:</span>
    <template
      v-for="batch in props.batches"
      :key="batch.name"
    >
      <span class="pb-0.5">
        <ColorSquare
          v-if="batch.color"
          :color="batch.color"
        />
      </span>
      <span>{{ batch.name }}</span>
    </template>
  </div>
</template>
